import React, { useState } from 'react';
import { API_ENDPOINTS } from '../config/backendConfig';

// Default export used like: const artworkHandler = ArtworkUploadHandler({...})
function ArtworkUploadHandler({
  currentSceneIndex,
  scenes,
  setScenes,
  setCurrentScene,
  storyId,
  originalArtwork,
  setOriginalArtwork,
  setModal,
  setError
}) {
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [pendingUploadTags, setPendingUploadTags] = useState([]);

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return imageUrl;
    if (imageUrl.startsWith('data:') || imageUrl.startsWith('blob:')) return imageUrl;
    if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
    if (imageUrl.startsWith('/uploads')) return `${API_ENDPOINTS.backendBase}${imageUrl}`;
    if (imageUrl.startsWith('uploads')) return `${API_ENDPOINTS.backendBase}/${imageUrl}`;
    if (!imageUrl.startsWith('/')) return `${API_ENDPOINTS.backendBase}/uploads/${imageUrl}`;
    return imageUrl;
  };

  // Handle file selection for upload (opens tagging modal upstream)
  const handleUploadArtwork = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return false;
    setPendingUploadFile(file);
    return true; // signal to open tagging UI
  };

  // Upload artwork after user adds tags
  const handleUploadAfterTagging = async (tags) => {
    if (!pendingUploadFile) return;
    setUploadingArtwork(true);
    try {
      const formData = new FormData();
      formData.append('artwork', pendingUploadFile);
      formData.append('sceneIndex', currentSceneIndex ?? 0);
      formData.append('storyId', storyId || '');
      formData.append('tags', JSON.stringify(tags || []));

      const response = await fetch(`${API_ENDPOINTS.backendBase}/api/upload-artwork`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Failed to upload artwork');
      const data = await response.json();
      if (!data.imageUrl) throw new Error('No image URL returned');

      if ((currentSceneIndex ?? 0) === 0) {
        setOriginalArtwork?.(data.imageUrl);
        if (scenes?.[0]) {
          const updatedScenes = [...scenes];
          updatedScenes[0] = {
            ...updatedScenes[0],
            imageUrl: data.imageUrl,
            taggedCharacters: tags,
          };
          setScenes?.(updatedScenes);
          setCurrentScene?.(updatedScenes[0]);
        }
      } else if (Array.isArray(scenes)) {
        const updatedScenes = [...scenes];
        if (updatedScenes[currentSceneIndex]) {
          updatedScenes[currentSceneIndex] = {
            ...updatedScenes[currentSceneIndex],
            imageUrl: data.imageUrl,
            taggedCharacters: tags,
          };
          setScenes?.(updatedScenes);
          setCurrentScene?.(updatedScenes[currentSceneIndex]);
        }
      }

      setModal?.({
        isOpen: true,
        title: '🎨 Artwork Uploaded & Tagged!',
        message: 'Your artwork and tags have been uploaded and set for this scene.',
        type: 'success',
      });
      setPendingUploadFile(null);
      setPendingUploadTags([]);
    } catch (error) {
      setModal?.({
        isOpen: true,
        title: '❌ Upload Failed',
        message: error.message || 'Failed to upload artwork.',
        type: 'error',
      });
      setError?.(error);
    } finally {
      setUploadingArtwork(false);
    }
  };

  // Tag an existing scene image (no upload)
  const handleExistingArtworkTagging = (tags) => {
    if (!Array.isArray(scenes)) return;
    const updatedScenes = [...scenes];
    if (updatedScenes[currentSceneIndex]) {
      updatedScenes[currentSceneIndex] = {
        ...updatedScenes[currentSceneIndex],
        taggedCharacters: tags,
      };
      setScenes?.(updatedScenes);
      setCurrentScene?.(updatedScenes[currentSceneIndex]);
    }
    setModal?.({
      isOpen: true,
      title: '🏷️ Characters Tagged!',
      message: `Successfully tagged ${tags.length} character(s) on Scene ${(currentSceneIndex ?? 0) + 1}!`,
      type: 'success',
    });
  };

  return {
    uploadingArtwork,
    pendingUploadFile,
    pendingUploadTags,
    setPendingUploadFile,
    setPendingUploadTags,
    handleUploadArtwork,
    handleUploadAfterTagging,
    handleExistingArtworkTagging,
    getFullImageUrl,
  };
}

export default ArtworkUploadHandler; 